import api from './axiosConfig';
import { storageService } from './storageService';
import { jwtUtils } from '../utils/jwtUtils';

export type UserRole = 'gensize_admin' | 'gensize_data_worker' | 'gensize_data_approver';

interface LoginResponse {
    access_token: string;
    token_type: string;
    password_expired: boolean;
    password_expiry_date: string;
}

interface LoginCredentials {
    username: string;
    password: string;
}

interface PasswordResetCredentials {
    token: string;
    new_password: string;
    new_password_confirm: string;
}

interface PasswordResetRequest {
    email: string;
    username: string;
}

interface UserData {
    id: string;
    username: string;
    roles: UserRole[]; // Changed from single role to array of roles
}

export const authService = {
    login: async (credentials: LoginCredentials): Promise<void> => {
        try {
            // Make the login request
            const response = await api.post<LoginResponse>('/login/', credentials);
            // Mock API response
            // const response = {
            //   data: {
            //     access_token: 'mock_jwt_token_12345',
            //     token_type: 'Bearer'
            //   }
            // };
            
            // Store the token and password expiry info
            storageService.setToken(response.data);
            storageService.setPasswordExpiry({
                password_expired: response.data.password_expired,
                password_expiry_date: response.data.password_expiry_date,
                dialog_shown: false, // Reset dialog shown flag on login
            });

            // Decode JWT token to extract user information
            const decodedUser = jwtUtils.decodeToken(response.data.access_token);
            
            if (!decodedUser) {
                throw new Error('Invalid token received from server');
            }

            // Map token roles to our UserRole type - store all valid roles
            const tokenRoles = decodedUser.roles;
            
            // Filter and validate roles from token
            const validRoles: UserRole[] = [];
            const validRoleTypes: UserRole[] = ['gensize_admin', 'gensize_data_worker', 'gensize_data_approver'];
            
            tokenRoles.forEach((role: string) => {
                if (validRoleTypes.includes(role as UserRole)) {
                    validRoles.push(role as UserRole);
                }
            });
            
            // Ensure at least one valid role exists - throw error if none found
            if (validRoles.length === 0) {
                throw new Error('Invalid user roles. User must have at least one valid role to proceed.');
            }

            const userData: UserData = {
                id: decodedUser.id,
                username: credentials.username,
                roles: validRoles, // Store all valid roles
            };

            storageService.setUser(userData);
        } catch (error: any) {
            // Handle specific login errors
            if (error.response?.status === 401) {
                throw new Error('Incorrect username or password. Please try again.');
            } else if (error.response?.status === 400) {
                throw new Error('Invalid login credentials. Please check your username and password.');
            } else if (error.response?.status === 422) {
                throw new Error('Invalid login format. Please check your input.');
            } else if (error.response?.status === 500) {
                throw new Error('Server error. Please try again later.');
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
                throw new Error('Network error. Please check your internet connection.');
            } else if (error.message) {
                // If there's a specific error message from the server
                throw new Error(error.message);
            } else {
                throw new Error('Login failed. Please try again.');
            }
        }
    },

    logout: () => {
        storageService.clearAuth();
    },

    hasRole: (requiredRoles: UserRole | UserRole[]): boolean => {
        const user = storageService.getUser();
        if (!user || !user.roles || user.roles.length === 0) return false;

        if (Array.isArray(requiredRoles)) {
            // Check if user has ANY of the required roles
            return requiredRoles.some(role => user.roles.includes(role));
        }
        // Check if user has the specific required role
        return user.roles.includes(requiredRoles);
    },

    // New method to get user info from token
    getUserFromToken: () => {
        const token = storageService.getToken();
        if (!token?.access_token) return null;
        
        return jwtUtils.decodeToken(token.access_token);
    },

    // New method to check if current token is expired
    isTokenExpired: (): boolean => {
        const token = storageService.getToken();
        if (!token?.access_token) return true;
        
        return jwtUtils.isTokenExpired(token.access_token);
    },

    // Password reset functionality
    resetPassword: async (credentials: PasswordResetCredentials): Promise<void> => {
        try {
            await api.post('/login/password/reset/token', credentials);
        } catch (error: any) {
            // Handle specific password reset errors
            if (error.response?.status === 400) {
                throw new Error('Invalid password reset request. Please check your input.');
            } else if (error.response?.status === 401) {
                throw new Error('Invalid or expired reset token. Please request a new password reset.');
            } else if (error.response?.status === 422) {
                throw new Error('Password does not meet requirements. Please ensure passwords match and meet security standards.');
            } else if (error.response?.status === 500) {
                throw new Error('Server error. Please try again later.');
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
                throw new Error('Network error. Please check your internet connection.');
            } else if (error.message) {
                // If there's a specific error message from the server
                throw new Error(error.message);
            } else {
                throw new Error('Password reset failed. Please try again.');
            }
        }
    },

    // Request password reset functionality
    requestPasswordReset: async (credentials: PasswordResetRequest): Promise<void> => {
        try {
            await api.post('/login/password/reset/email', credentials);
        } catch (error: any) {
            // Handle specific password reset request errors
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.detail || 'Invalid request. Please check your input.';
                if (errorMessage.includes('User not found')) {
                    throw new Error('User not found. Please check your username.');
                } else if (errorMessage.includes('Email does not match')) {
                    throw new Error('Email does not match the registered email for this username.');
                } else {
                    throw new Error(errorMessage);
                }
            } else if (error.response?.status === 422) {
                throw new Error('Invalid email format. Please check your email address.');
            } else if (error.response?.status === 500) {
                throw new Error('Server error. Please try again later.');
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
                throw new Error('Network error. Please check your internet connection.');
            } else if (error.message) {
                // If there's a specific error message from the server
                throw new Error(error.message);
            } else {
                throw new Error('Failed to send password reset request. Please try again.');
            }
        }
    },
};
